import { constants } from '@wsb/guac-widget-core';
const { Z_INDEX_NAV_DRAWER } = constants.layers;
import { NAV_DRAWER_OFFSET } from '../../../constants';
import {
  COLOR_MODE_CSS_VAR_NAMES,
  colorModeColor,
  cssVarValue,
  getColorModeCssVariables
} from '../../../utils/colorMode';

const COLOR_MODE_BODY_VARS = COLOR_MODE_CSS_VAR_NAMES.body || {};
const COLOR_MODE_TYPOGRAPHY_VARS = COLOR_MODE_CSS_VAR_NAMES.typography || {};
const COLOR_MODE_INPUT_VARS = COLOR_MODE_CSS_VAR_NAMES.input || {};
const COLOR_MODE_BG_VAR = COLOR_MODE_BODY_VARS.bg || '--wsb-bg';
const COLOR_MODE_BORDER_VAR = COLOR_MODE_BODY_VARS.border || '--wsb-border';
const COLOR_MODE_TEXT_VAR = COLOR_MODE_TYPOGRAPHY_VARS.body || '--wsb-text';
const COLOR_MODE_INPUT_BG_VAR = COLOR_MODE_INPUT_VARS.bg || '--wsb-input-bg';

// The drawer is a fixed, full-screen panel rendered as a SIBLING of the <nav> (see
// guac-widget-nav's NavBar), so it sits outside the subtree where the nav declares its
// color-mode `--wsb-*` block. Custom properties only inherit downward, so the drawer has
// to declare the mode's block itself — the same thing Section does for a section widget.
//
// Declaring it on the ROOT (rather than per element) also lights up every descendant that
// already reads those vars or `currentColor`: links/sublinks via NavBeta/SubNavBeta, social
// and flyout icons via `fill: currentColor`, and the glass CTA/input via `base.colorMode`,
// which propagates because `colorMode` is a context prop and props beat context.
//
// Returns null when the theme has no color-mode dials, so legacy layouts stay byte-identical.
function getDrawerColorModeStyle(theme, colorMode) {
  const vars = colorMode ? getColorModeCssVariables(theme, colorMode) : null;
  if (!vars) return null;

  return {
    ...vars,
    backgroundColor: cssVarValue(COLOR_MODE_BG_VAR),
    color: cssVarValue(COLOR_MODE_TEXT_VAR)
  };
}

export function NavigationDrawer({ category = 'accent', colorMode, left = true, ...props }) {
  const colorModeStyle = getDrawerColorModeStyle(this, colorMode);
  return this.merge(
    {
      category,
      left,
      section: 'default',
      // `colorMode` is a real context prop, so setting it here scopes the mode to the
      // drawer subtree — that is what lets descendants resolve their own color-mode
      // values (glass CTA / input light-dark) against the drawer rather than the nav.
      ...(colorMode && { 'colorMode': colorMode, 'data-color-mode': colorMode }),
      style: {
        ...(colorModeStyle || { backgroundColor: 'section' }),
        position: 'fixed',
        top: 0,
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        zIndex: Z_INDEX_NAV_DRAWER,
        paddingTop: 'xxlarge',
        WebkitOverflowScrolling: 'touch',
        transform: `translateX(${NAV_DRAWER_OFFSET})`,
        overscrollBehavior: 'contain',
        boxShadow: '0 2px 6px 0px rgba(0,0,0,0.2)',
        transition: 'transform .3s ease-in-out',
        visibility: 'hidden'
      },
      role: 'navigation'
    },
    props
  );
}

export function NavigationDrawerList(props) {
  return this.List(
    this.merge(
      {
        role: 'menu'
      },
      props
    )
  );
}

export function NavigationDrawerListItem(props) {
  return this.ListItem(
    this.merge(
      {
        style: {
          'display': 'block',
          'marginBottom': '0',
          // Reads the drawer's mode border when one is active (the root scopes
          // `colorMode` for this subtree); legacy themes keep the overlay token.
          'borderColor': colorModeColor(this, COLOR_MODE_BORDER_VAR, 'uLowContrastOverlay'),
          'borderBottomWidth': 1,
          'borderBottomStyle': 'solid',
          ':last-child': {
            borderBottom: 'none'
          }
        },
        role: 'menuitem'
      },
      props
    )
  );
}

export function NavigationDrawerLink(props) {
  return this.Link(
    this.merge(
      {
        typography: 'NavBeta',
        style: {
          paddingVertical: 'small',
          paddingHorizontal: 'medium',
          display: 'flex',
          alignItems: 'center',
          minWidth: 200,
          justifyContent: 'space-between',
          ['@md']: {
            minWidth: 300
          }
        }
      },
      props
    )
  );
}

export function NavigationDrawerLinkActive(props) {
  return this.NavigationDrawerLink(
    this.merge(
      {
        active: true
      },
      props
    )
  );
}

export function NavigationDrawerSubLink(props) {
  return this.NavigationDrawerLink(
    this.merge(
      {
        typography: 'SubNavBeta',
        style: {
          display: 'block',
          paddingVertical: 'xsmall',
          paddingLeft: 'xlarge',
          paddingRight: 'medium'
        }
      },
      props
    )
  );
}

export function NavigationDrawerSubLinkActive(props) {
  return this.NavigationDrawerSubLink(
    this.merge(
      {
        active: true
      },
      props
    )
  );
}

export function NavigationDrawerInputSearch(props) {
  return this.Input(
    this.merge(
      {
        style: {
          backgroundColor: colorModeColor(this, COLOR_MODE_INPUT_BG_VAR, 'searchMobileDark'),
          borderWidth: 'none',
          borderRadius: 'none',
          paddingVertical: 'small',
          paddingHorizontal: 'xlarge'
        },
        role: 'searchbox'
      },
      props
    )
  );
}
